/**
 * Keeps identifying data out of Plausible (GIFTWEB-33).
 *
 * Two things are guarded, because there are two ways data escapes:
 *
 * 1. The **URL**, which Plausible sends with every event whether we ask it to
 *    or not. Ours carries `?code=<invite_code>` — the link a parent shares,
 *    which names one specific child — and `?payment_id=<uuid>`. Neither has
 *    any business in an analytics report.
 * 2. The **custom properties** we attach ourselves. The type of `track` stops
 *    a call site inventing an `email` prop, but types are a build-time promise
 *    and this runs at the tracker boundary, so it also catches anything that
 *    reaches `window.plausible` directly.
 */

/**
 * Query parameters worth keeping.
 *
 * An allowlist rather than a denylist: a new identifying parameter added to a
 * route six months from now should be dropped by default, not leak until
 * somebody remembers this file.
 */
const SAFE_QUERY_PARAMS = new Set(['status', 'error']);

/** Property names that would carry a person, whatever the value looks like. */
const PII_KEY_PATTERN =
  /(email|phone|mobile|name|address|postcode|dob|birth|code|token|uuid)/i;

/**
 * Names that are an identifier.
 *
 * Case-sensitive on the camelCase form on purpose: `contributionId` is an
 * identifier, `valid` is not, and a case-insensitive `id$` cannot tell them
 * apart.
 */
const ID_KEY_PATTERN = /(^id$|^ID$|_id$|-id$|Id$|ID$)/;

/** Values that are identifying whatever they are called. */
const PII_VALUE_PATTERNS: RegExp[] = [
  // Email.
  /[^\s@]+@[^\s@]+\.[^\s@]+/,
  // Phone: an optional +, then 9 or more digits once spacing is removed.
  /^\+?[\d\s()-]{9,}$/,
  // UUID — contribution and payment ids.
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i,
  // Invite codes: long opaque mixed-case strings, e.g. 3Qi1f9vjTn3Vcpm7Xz8PVG.
  /^(?=.*[a-z])(?=.*[A-Z])[A-Za-z0-9_-]{16,}$/,
];

/**
 * Strips a URL back to what a funnel report needs: where the visitor was, and
 * the outcome parameters that say which variant of the page they saw.
 */
export function sanitiseUrl(raw: string): string {
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    // Not a URL we can reason about, so nothing of it is safe to report.
    return '';
  }

  const kept = new URLSearchParams();
  for (const [key, value] of url.searchParams) {
    if (SAFE_QUERY_PARAMS.has(key)) kept.append(key, value);
  }

  const query = kept.toString();
  // The hash is dropped whole: TrueLayer returns through one carrying
  // `payment_id` and `resource_token`.
  return `${url.origin}${url.pathname}${query ? `?${query}` : ''}`;
}

/**
 * Names the properties that must not be sent, or an empty list when the set is
 * clean. Returning the offenders rather than a boolean so the caller can say
 * which one it was.
 */
export function findPii(props?: Record<string, unknown>): string[] {
  if (!props) return [];

  const offenders: string[] = [];
  for (const [key, value] of Object.entries(props)) {
    if (PII_KEY_PATTERN.test(key) || ID_KEY_PATTERN.test(key)) {
      offenders.push(key);
      continue;
    }
    if (typeof value !== 'string') continue;
    const trimmed = value.trim();
    if (!trimmed) continue;
    if (PII_VALUE_PATTERNS.some((pattern) => pattern.test(trimmed))) {
      offenders.push(key);
    }
  }
  return offenders;
}
